import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import MiniToggle from './MiniToggle';
import type { TenantUser } from './ClientUsersTab';

interface Props {
  tenantId: string;
  requireMfa: boolean;
  onTenantUpdated: (patch: { requireMfa: boolean }) => void;
}

export default function ClientSecurityTab({ tenantId, requireMfa, onTenantUpdated }: Props) {
  const { t } = useTranslation();
  const [users, setUsers]     = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/tenants/${tenantId}/users`);
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const toggleTenantMfa = async () => {
    const { data } = await api.put(`/admin/tenants/${tenantId}`, { requireMfa: !requireMfa });
    onTenantUpdated({ requireMfa: data.requireMfa });
  };

  const toggleUserMfa = async (u: TenantUser) => {
    await api.patch(`/admin/tenants/${tenantId}/users/${u.id}`, { mfaRequired: !u.mfaRequired });
    load();
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t('admin.security.title')}</h2>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{t('tenants.requireMfa')}</div>
          <div style={{ fontSize: 11, color: 'var(--gray)' }}>{t('admin.security.tenantMfaHint')}</div>
        </div>
        <MiniToggle value={requireMfa} onChange={toggleTenantMfa} title={t('tenants.requireMfa')} />
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{t('admin.security.perUser')}</div>

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : (
        <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {users.filter(u => u.role !== 'super_admin').map((u, i, arr) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</div>
              </div>
              <span style={{ fontSize: 9, color: 'var(--gray)', whiteSpace: 'nowrap' }}>{t('tenants.usersModal.requireMfa')}</span>
              <MiniToggle value={u.mfaRequired} onChange={() => toggleUserMfa(u)} title={t('tenants.usersModal.requireMfa')} />
            </div>
          ))}
          {users.filter(u => u.role !== 'super_admin').length === 0 && (
            <div style={{ padding: '16px', fontSize: 13, color: 'var(--gray)' }}>{t('tenants.usersModal.noUsers')}</div>
          )}
        </div>
      )}
    </div>
  );
}
